/**
 * The segmented controls (.segmented: a schedule's cadence, Usage's window, an upgrade's wood
 * amounts, a luxury task's gold) are radio groups: the group has role="radiogroup" and a name, each
 * option role="radio" and aria-checked. Every option stays a Tab stop and a press (Enter, Space, a
 * tap) picks it; the arrow keys also move along the group, picking as they go, as a native radio
 * group does. `radioKeys` is the group's onKeyDown.
 */

const NEXT = new Set(["ArrowRight", "ArrowDown"]);
const PREV = new Set(["ArrowLeft", "ArrowUp"]);

/**
 * Where an arrow key goes from option `current` among options that may be disabled: the next (or
 * previous) enabled one, wrapping; Home and End the first and last enabled. Null for any other key,
 * or when no other option is enabled.
 */
export function radioStep(enabled: readonly boolean[], current: number, key: string): number | null {
  const n = enabled.length;
  if (n === 0) return null;
  if (key === "Home" || key === "End") {
    const order = key === "Home" ? [...enabled.keys()] : [...enabled.keys()].reverse();
    const hit = order.find((i) => enabled[i]);
    return hit === undefined || hit === current ? null : hit;
  }
  const dir = NEXT.has(key) ? 1 : PREV.has(key) ? -1 : 0;
  if (dir === 0) return null;
  const from = current < 0 ? (dir > 0 ? -1 : n) : current;
  for (let step = 1; step <= n; step++) {
    const i = (((from + dir * step) % n) + n) % n;
    if (enabled[i] && i !== current) return i;
  }
  return null;
}

/** onKeyDown for a role="radiogroup": the arrows, Home and End move to an option and pick it. */
export function radioKeys(e: KeyboardEvent): void {
  const group = e.currentTarget as HTMLElement | null;
  if (!group) return;
  const options = Array.from(group.querySelectorAll<HTMLButtonElement>("[role='radio']"));
  const current = options.indexOf(document.activeElement as HTMLButtonElement);
  const to = radioStep(
    options.map((o) => !o.disabled),
    current,
    e.key,
  );
  if (to === null) return;
  e.preventDefault();
  options[to].focus();
  options[to].click();
}
